import React, { useState } from 'react';
import { Box, IconButton, Icon, Typography, Collapse, useTheme, Tooltip, Chip, Avatar } from '@mui/material';
import { alpha } from '@mui/material/styles';
import CustomMarkdown from '../../CustomMarkdown'; // Corrected path
import { useColorMode } from 'src/theme/ThemeProvider';
import { useAppDispatch, useAppSelector } from 'src/store';
import { deleteMessage } from 'src/features/account/messages.slice';
import { decrementMessageCount } from 'src/features/account/conversations.slice';
import MessageOptions from './MessageOptions';
import MessageProgress from './MessageProgress';
import MessageReadReceipts from './MessageReadReceipts';
import CalendarView from './CalendarView';
import MessageMetadataDebug from './MessageMetadataDebug';
import { keyframes } from '@mui/system';

interface FunctionMessageProps {
    message: any; // Consider defining a more specific type for message
    channelId?: string;
    conversationDeliveryState?: string;
}

// Success icon pop-in animation
const successPopIn = keyframes`
  0% {
    transform: scale(0);
    opacity: 0;
  }
  60% {
    transform: scale(1.2);
    opacity: 1;
  }
  100% {
    transform: scale(1);
    opacity: 1;
  }
`;

const FunctionMessage: React.FC<FunctionMessageProps> = ({ message, channelId, conversationDeliveryState }) => {
    const [expanded, setExpanded] = useState(false);
    const [showDebug, setShowDebug] = useState(false);
    const [showJsonResponse, setShowJsonResponse] = useState(false);
    const theme = useTheme();
    const { mode } = useColorMode();
    const isDarkMode = mode === 'dark';
    const dispatch = useAppDispatch();
    const { user } = useAppSelector(state => state.user);

    const toggleExpanded = () => setExpanded(!expanded);

    const resolvedChannelId = channelId || message.channel_id;

    const setIconUrl = () => {
        const content = message.json_content?.icon_url || "";
        return content;
    };
    const setIconTooltip = () => {
        const content = message.json_content?.icon_tooltip || "";
        return content;
    };
    const iconUrl = setIconUrl();
    const iconTooltip = setIconTooltip();


    /**
     * Extracts JSON response data from function messages for display in dropdown
     * 
     * @returns {Array|Object|null} Function response data or null if none available
     * 
     * Purpose:
     * - Provides access to full, untruncated function execution responses
     * - Supports both new format (json_content field) and legacy format (metadata)
     * - Enables detailed debugging of function calls without cluttering main UI
     * - Returns structured data for JSON viewer dropdown component
     * 
     * Data sources (in priority order):
     * 1. message.json_content - New format with structured function response data
     * 2. message.metadata.function_responses - Structured response objects with metadata
     * 3. message.metadata.chosen_functions_responses - Legacy format fallback
     * 
     * Example return formats:
     * - Array: [{ function_name: 'web_search', response: {...}, executed_at: '...' }]
     * - Object: { function_name: 'web_search', response: {...} }
     * - null: No function response data available
     * 
     * Usage: const jsonData = extractJsonResponses(); // Used by JSON dropdown component
     */
    const extractJsonResponses = () => {
        // First check json_content field (new format)
        if (message.json_content) {
            return message.json_content;
        }
        
        // Fallback: extract from metadata function responses (existing format)
        const functionResponses = message.metadata?.function_responses || message.metadata?.chosen_functions_responses;
        if (functionResponses && Array.isArray(functionResponses) && functionResponses.length > 0) {
            return functionResponses;
        }
        
        return null;
    };

    const jsonResponses = extractJsonResponses();
    const hasJsonResponses = jsonResponses && (Array.isArray(jsonResponses) ? jsonResponses.length > 0 : true);

    const handleDelete = () => {
        if (!message.id) return;
        dispatch(deleteMessage({ 
            messageId: message.id, 
            channelId: resolvedChannelId, // May be undefined; backend handles optional channel
            conversationId: message.conversation_id 
        }));
        dispatch(decrementMessageCount({ conversationId: message.conversation_id }));
    };

    // Check if this is a calendar function with calendar_data
    const hasCalendarData = message.metadata?.calendar_data && 
                           message.metadata.function_name === 'get_available_timeslots' &&
                           (Array.isArray(message.metadata.calendar_data.available_slots) || 
                            typeof message.metadata.calendar_data.available_slots === 'string');

    // Debug logging for calendar data detection
    if (message.metadata?.function_name === 'get_available_timeslots') {
        console.log('🔍 Calendar function detected:', {
            messageId: message.id,
            hasMetadata: !!message.metadata,
            hasCalendarData: !!message.metadata?.calendar_data,
            functionName: message.metadata?.function_name,
            availableSlots: message.metadata?.calendar_data?.available_slots,
            isArray: Array.isArray(message.metadata?.calendar_data?.available_slots),
            isString: typeof message.metadata?.calendar_data?.available_slots === 'string',
            finalResult: hasCalendarData,
            fullMetadata: message.metadata
        });
    }

    return (
        <Box sx={{
            width: '100%',
            mb: 0.5, // Reduced from 1
            pl: '4px', // Align slightly with other messages
            pr: { xs: '4px', sm: 0 }, // Right padding on mobile
            position: 'relative',
            opacity: 1, // Changed from 0.7 to 1 - making function messages fully visible
            '&:hover .expand-button, &:hover .action-buttons': {
                opacity: 1,
            }
        }}
        data-message-type="function"
        >
            {/* Debug metadata for admin users (role 11) */}
            {user?.role === 11 && (
                <Box sx={{ 
                    position: 'relative',
                    mb: showDebug ? 2 : 0,
                }}>
                    {showDebug &&
                        <Box>
                            <Typography>
                                created_at:{message.created_at}
                            </Typography>
                            <Typography>
                                updated_at:{message.updated_at}
                            </Typography>
                            <Typography>
                                deliver_after:{message.deliver_after}
                            </Typography>
                            <Typography>
                                delivered_at:{message.delivered_at}
                            </Typography>
                            <Typography>
                                sort_time_used:{message.sort_time_used}
                            </Typography>
                        </Box>
                    }
                    {/* Collapsed view with toggle button */}
                    {!showDebug && (
                        <Box sx={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            justifyContent: 'flex-end',
                            mb: 1,
                            gap: 1,
                        }}>
                            {/* Show token count when collapsed */}
                            {message.metadata?.consumption && (
                                <Typography 
                                    variant="caption" 
                                    sx={{ 
                                        color: 'text.secondary', 
                                        opacity: 0.5,
                                        fontSize: '11px',
                                    }}
                                >
                                    {(() => {
                                        const consumption = message.metadata.consumption || {};
                                        const totalTokens = Object.values(consumption).reduce(
                                            (sum: number, step: any) => sum + (step?.input_tokens || 0) + (step?.output_tokens || 0), 0
                                        ) as number;
                                        return `${totalTokens.toLocaleString()} tokens`;
                                    })()}
                                </Typography>
                            )}
                            <IconButton
                                size="small"
                                onClick={() => setShowDebug(true)}
                                sx={{
                                    opacity: 0.3,
                                    transition: 'opacity 0.3s',
                                    '&:hover': { opacity: 0.8 },
                                    padding: '4px',
                                }}
                            >
                                <Icon sx={{ fontSize: '16px' }}>code</Icon>
                            </IconButton>
                        </Box>
                    )}
                    
                    {/* Expanded debug view */}
                    {showDebug && (
                        <Box sx={{ position: 'relative' }}>
                            <IconButton
                                size="small"
                                onClick={() => setShowDebug(false)}
                                sx={{
                                    position: 'absolute',
                                    top: 8,
                                    right: 8,
                                    zIndex: 1,
                                    bgcolor: theme.palette.background.paper,
                                    border: `1px solid ${theme.palette.divider}`,
                                    '&:hover': { 
                                        bgcolor: theme.palette.action.hover,
                                    },
                                }}
                            >
                                <Icon sx={{ fontSize: '16px' }}>close</Icon>
                            </IconButton>
                            <MessageMetadataDebug message={message} />
                        </Box>
                    )}
                </Box>
            )}
            
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                {/* <IconButton
                    size="small"
                    onClick={toggleExpanded}
                    className="expand-button"
                    sx={{
                        opacity: 0.5,
                        transition: 'opacity 0.3s',
                        color: isDarkMode ? 'text.secondary' : 'rgba(0, 0, 0, 0.6)',
                        mt: 0.5, // Align with the first line of text
                    }}
                >
                    <Icon sx={{ fontSize: '16px' }}>{expanded ? 'code_off' : 'code'}</Icon>
                </IconButton> */}
                
                <Box 
                    sx={{ 
                        flex: 1,
                        cursor: 'pointer',
                        '&:hover': { opacity: 0.9 }
                    }}
                    onClick={toggleExpanded}
                >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Chip
                        label={iconTooltip}
                        size="small"
                        variant="outlined"
                        avatar={
                            <Avatar
                                sx={{
                                    width: 24,
                                    height: 24,
                                    bgcolor: '#fff',
                                    border: `1px solid ${theme.palette.divider}`,
                                }}
                            >
                                {iconUrl ? (
                                    <Box 
                                        component="img" 
                                        src={iconUrl} 
                                        alt={iconTooltip || 'Function icon'} 
                                        sx={{ width: 14, height: 14 }} 
                                    />
                                ) : (
                                    <Icon sx={{ fontSize: 16, color: 'text.secondary' }}>apps</Icon>
                                )}
                            </Avatar>
                        }
                    />
                </Box>
                </Box>
            </Box>

            <Collapse in={expanded} timeout="auto" unmountOnExit>
                <Box
                    sx={{
                        mt: { xs: 0.5, sm: 1 },
                        ml: { xs: 3, sm: 4 }, // Indent the raw content
                        p: { xs: 1, sm: 1.5 },
                        borderRadius: '4px',
                        backgroundColor: isDarkMode ? alpha(theme.palette.background.paper, 0.5) : alpha(theme.palette.grey[200], 0.7),
                        overflowX: 'hidden', // Prevent horizontal scroll
                        overflowY: 'auto', // Allow vertical scroll
                        maxHeight: '500px', // Limit height
                        wordBreak: 'break-word',
                        whiteSpace: 'pre-wrap',
                        overflowWrap: 'anywhere',
                        '& pre': {
                            whiteSpace: 'pre-wrap',
                            wordBreak: 'break-word',
                            overflowWrap: 'anywhere',
                        },
                        '& code': {
                            whiteSpace: 'pre-wrap',
                            wordBreak: 'break-word',
                            overflowWrap: 'anywhere',
                        },
                        '&::-webkit-scrollbar': { width: '4px', height: '4px' },
                        '&::-webkit-scrollbar-thumb': { backgroundColor: theme.palette.grey[500], borderRadius: '2px' },
                    }}
                >
                    {/* Render raw_content using CustomMarkdown */}
                    <CustomMarkdown>{message.raw_content || "No raw content available."}</CustomMarkdown>
                </Box>
                {/* JSON Response Section */}
                {/* JSON Response Button */}
                {hasJsonResponses && (
                    <Tooltip title="View JSON Response">
                        <IconButton
                            size="small"
                            onClick={() => setShowJsonResponse(!showJsonResponse)}
                            className="expand-button"
                            sx={{
                                opacity: 0.5,
                                transition: 'opacity 0.3s',
                                color: showJsonResponse ? theme.palette.primary.main : (isDarkMode ? 'text.secondary' : 'rgba(0, 0, 0, 0.6)'),
                                mt: 0.5,
                            }}
                        >
                            <Icon sx={{ fontSize: '16px' }}>data_object</Icon>
                        </IconButton>
                    </Tooltip>
                )}
                {hasJsonResponses && (
                    <Collapse in={showJsonResponse} timeout="auto" unmountOnExit>
                        <Box
                            sx={{
                                mt: 1,
                                ml: { xs: 3, sm: 4 },
                                p: 1.5,
                                borderRadius: '4px',
                                backgroundColor: isDarkMode
                                    ? alpha(theme.palette.info.main, 0.1)
                                    : alpha(theme.palette.info.main, 0.05),
                                border: `1px solid ${alpha(theme.palette.info.main, 0.3)}`,
                                maxHeight: '600px',
                                overflowY: 'auto',
                                '&::-webkit-scrollbar': { width: '6px' },
                                '&::-webkit-scrollbar-thumb': {
                                    backgroundColor: alpha(theme.palette.info.main, 0.5),
                                    borderRadius: '3px'
                                },
                            }}
                        >
                            <Typography
                                variant="caption"
                                sx={{
                                    color: theme.palette.info.main,
                                    fontWeight: 600,
                                    display: 'block',
                                    mb: 1.5,
                                    fontSize: '0.75rem',
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.5px'
                                }}
                            >
                                Function JSON Responses
                            </Typography>
                            <pre
                                style={{
                                    margin: 0,
                                    padding: 0,
                                    fontSize: '11px',
                                    fontFamily: 'Consolas, Monaco, "Courier New", monospace',
                                    whiteSpace: 'pre-wrap',
                                    wordBreak: 'break-word',
                                    color: isDarkMode ? '#e1e4e8' : '#24292e',
                                    lineHeight: 1.4,
                                }}
                            >
                                {JSON.stringify(jsonResponses, null, 2)}
                            </pre>
                        </Box>
                    </Collapse>
                )}
            </Collapse>

            {/* Render CalendarView if calendar data is available */}
            {hasCalendarData && (
                <Box sx={{ 
                    mt: 2, 
                    ml: { xs: 0 }, // Remove sm: 4 to make full width on desktop
                    mr: { xs: 0 }  // Remove sm: 4 to make full width on desktop
                }}>
                    <CalendarView 
                        calendarData={message.metadata.calendar_data}
                        conversationId={message.conversation_id}
                        channelId={resolvedChannelId}
                        messageId={message.id}
                    />
                </Box>
            )}

            {/* Progress Bar, Success Icon or Error Icon */}
            {(() => {
                const metadata = message.metadata || {};

                // Coerce values & perform type-safe checks
                const progressVal = metadata.progress !== undefined && !isNaN(Number(metadata.progress))
                    ? Number(metadata.progress)
                    : undefined;

                const isAborted = metadata.step === 'aborted';
                const failedStepExists = typeof metadata.failed_step === 'string' && metadata.failed_step.length > 0;
                const errorMessageExists = typeof metadata.error_message === 'string' && metadata.error_message.length > 0;
                const retryAborted = metadata.retry_aborted === true;

                const hasError = isAborted || failedStepExists || errorMessageExists || retryAborted;

                // Prioritise error display over others
                if (hasError) {
                    // Build comprehensive error tooltip content
                    const errorDetails: string[] = [];
                    
                    if (metadata.error_message) {
                        errorDetails.push(`Error: ${metadata.error_message}`);
                    }
                    
                    if (metadata.failed_step) {
                        errorDetails.push(`Failed Step: ${metadata.failed_step}`);
                    }
                    
                    if (metadata.abort_reason) {
                        errorDetails.push(`Abort Reason: ${metadata.abort_reason}`);
                    }
                    
                    if (metadata.cancel_reason) {
                        errorDetails.push(`Cancel Reason: ${metadata.cancel_reason}`);
                    }
                    
                    if (metadata.failed_functions && Array.isArray(metadata.failed_functions) && metadata.failed_functions.length > 0) {
                        errorDetails.push(`Failed Functions: ${metadata.failed_functions.join(', ')}`);
                    }
                    
                    if (metadata.retries !== undefined) {
                        errorDetails.push(`Retries Attempted: ${metadata.retries}`);
                    }
                    
                    if (metadata.retry_aborted) {
                        errorDetails.push(`Retry Limit Exceeded: Yes`);
                    }
                    
                    if (metadata.cancelled) {
                        errorDetails.push(`Status: Cancelled`);
                    }
                    
                    if (isAborted && !metadata.abort_reason) {
                        errorDetails.push(`Status: Aborted`);
                    }
                    
                    // If no specific error details found, add a generic message
                    if (errorDetails.length === 0) {
                        errorDetails.push('An error occurred during execution');
                    }
                    
                    return (
                        <Box sx={{ ml: { xs: 3, sm: 4 }, mr: { xs: 3, sm: 4 }, display: 'flex', justifyContent: 'start' }}>
                            <Tooltip 
                                title={
                                    <Box sx={{ p: 1 }}>
                                        {errorDetails.map((detail, index) => (
                                            <Typography 
                                                key={index} 
                                                variant="body2" 
                                                sx={{ 
                                                    mb: index < errorDetails.length - 1 ? 0.5 : 0,
                                                    fontSize: '0.875rem' 
                                                }}
                                            >
                                                {detail}
                                            </Typography>
                                        ))}
                                    </Box>
                                }
                                placement="top"
                                arrow
                            >
                                <Icon
                                    sx={{
                                        color: theme.palette.error.main,
                                        fontSize: { xs: 20, sm: 24 },
                                        cursor: 'help', // Show help cursor on hover
                                    }}
                                >
                                    error
                                </Icon>
                            </Tooltip>
                        </Box>
                    );
                }

                // Show success when progress === 100 (or greater) and no errors present
                if (progressVal !== undefined && progressVal >= 100) {
                    return (
                        <Box sx={{ ml: { xs: 3, sm: 4 }, mr: { xs: 3, sm: 4 }, display: 'flex', justifyContent: 'start' }}>
                            <Icon
                                sx={{
                                    color: theme.palette.success.main,
                                    animation: `${successPopIn} 0.6s ease-out`,
                                    fontSize: { xs: 20, sm: 24 },
                                }}
                            >
                                check_circle
                            </Icon>
                        </Box>
                    );
                }

                // Show progress bar when we have a numeric progress < 100
                if (progressVal !== undefined) {
                    return (
                        <Box sx={{ ml: { xs: 3, sm: 4 }, mr: { xs: 3, sm: 4 } }}>
                            <MessageProgress progress={progressVal} />
                        </Box>
                    );
                }

                // Nothing to render if no relevant metadata is present
                return null;
            })()}

            {/* Message Options */}
            <MessageOptions 
                message={message}
                onDelete={handleDelete}
                showEdit={false}
                showSend={false}
                channelId={channelId}
                conversationDeliveryState={conversationDeliveryState}
            />
        </Box>
    );
};

export default React.memo(FunctionMessage, (prevProps, nextProps) => {
  return (
    prevProps.message?.id === nextProps.message?.id &&
    prevProps.channelId === nextProps.channelId &&
    prevProps.conversationDeliveryState === nextProps.conversationDeliveryState &&
    JSON.stringify(prevProps.message) === JSON.stringify(nextProps.message)
  );
}); 
